import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportDynamicPricing } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRate, fmtDateShort } from "../shared";

const PAGE_SIZE = 15;

// pricing_action strings can have suffixes like "Raise 15%" — match on prefix.
const actionTone = (action) => {
  if (!action) return { badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300", row: "" };
  if (action.startsWith("HIGH DEMAND")) {
    return {
      badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
      row: "bg-red-50/60 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20",
    };
  }
  if (action.startsWith("DEMAND BUILDING")) {
    return {
      badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
      row: "bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20",
    };
  }
  if (action.startsWith("WEEKEND PREMIUM")) {
    return {
      badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
      row: "bg-blue-50/60 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20",
    };
  }
  return {
    badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    row: "hover:bg-gray-50 dark:hover:bg-gray-800/40",
  };
};

const ActionBadge = ({ action }) => {
  const tone = actionTone(action);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${tone.badge}`}>
      {action || "—"}
    </span>
  );
};

const DynamicPricingBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No dynamic pricing recommendations for the selected category." />;
  }

  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full min-w-[1200px] text-sm table-fixed responsive-card-table">
        <colgroup>
          <col style={{ width: "8%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2.5 text-left font-semibold">Date</th>
            <th className="px-3 py-2.5 text-left font-semibold">Window</th>
            <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
            <th className="px-3 py-2.5 text-right font-semibold">Suggested</th>
            <th className="px-3 py-2.5 text-right font-semibold">Change</th>
            <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
            <th className="px-3 py-2.5 text-right font-semibold">Baseline</th>
            <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail.</th>
            <th className="px-3 py-2.5 text-right font-semibold">Comp Occupancy</th>
            <th className="px-3 py-2.5 text-left font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const tone = actionTone(row.pricing_action);
            const delta = row.suggested_price != null && row.current_price != null
              ? Number(row.suggested_price) - Number(row.current_price)
              : null;
            const deltaTone = delta == null
              ? "text-gray-500 dark:text-gray-400"
              : delta > 0
                ? "text-emerald-600 dark:text-emerald-400"
                : delta < 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-500 dark:text-gray-400";
            return (
              <tr key={`${row.check_in}-${index}`} className={`border-b dark:border-gray-800 transition ${tone.row}`}>
                <td data-label="Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDateShort(row.check_in)}</td>
                <td data-label="Window" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 truncate" title={row.date_window_name || ""}>{row.date_window_name || "—"}</td>
                <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.current_price)}</td>
                <td data-label="Suggested" className="px-3 py-2.5 text-right font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.suggested_price)}</td>
                <td data-label="Change" className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${deltaTone}`}>
                  {delta == null ? "—" : `${delta > 0 ? "+" : delta < 0 ? "−" : ""}$${Math.abs(delta).toFixed(2)}`}
                </td>
                <td data-label="Market Avg" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg)}</td>
                <td data-label="Baseline" className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.category_baseline)}</td>
                <td data-label="Limited Avail." className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.sold_out_count ?? 0}</td>
                <td data-label="Comp Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.comp_occupancy} /></td>
                <td data-label="Action" className="px-3 py-2.5 whitespace-nowrap"><ActionBadge action={row.pricing_action} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ThinScroll>
  );
};

const DynamicPricingBodyPaginated = ({ rows }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <DynamicPricingBody rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </>
  );
};

const DynamicPricingPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`dynamic-pricing-${clientId}-${categoryCode || "all"}`}
    title="Dynamic Pricing"
    tip="Algorithmic suggested rate per date with a one-line rationale (HIGH DEMAND, WEEKEND PREMIUM, HOLD AT MARKET, etc.). Rows where Suggested ≠ Current are the actionable ones."
    description="Algorithmic price suggestions per date with the rationale behind each move."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportDynamicPricing(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : (data?.items ?? []);
      return (
        <>
          <PanelTakeaway panelKey="dynamic_pricing" say={say} />
          <DynamicPricingBodyPaginated rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default DynamicPricingPanel;
